import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import trackEvents from '../../constants/track-events'
import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { trackEvent } from '../../helpers/analytics'
import { ROLES } from '../../helpers/authorization-roles'
import {
  PARTNER_TABLE_COLUMNS_KEY,
  TABLE_COLUMN_CATALOG,
  TABLE_IDS,
  readPartnerTableColumns
} from '../../helpers/table-column-settings'
import { useMutationAndUpdateQuery } from '../../hooks/use-mutation-and-update-query'
import Divider from '../divider'
import GroupLabel from '../tailwind/GroupLabel'
import Toggle from '../tailwind/toggle'
import SettingsContainer from './settings-container'

const I18N_PREFIX = 'components.settings.table_columns.'

const TABLES = [
  { id: TABLE_IDS.TALENTS, groupKey: 'group_talents' },
  { id: TABLE_IDS.CAREERS, groupKey: 'group_careers' }
]

// Formik-Werte: pro Tabelle eine Map id -> verfügbar (true) oder deaktiviert.
const toFormValues = (disabledByTable) =>
  Object.fromEntries(
    TABLES.map(({ id: tableId }) => [
      tableId,
      Object.fromEntries(
        TABLE_COLUMN_CATALOG[tableId].map((column) => [
          column.id,
          !disabledByTable[tableId].includes(column.id)
        ])
      )
    ])
  )

const toDisabledSettings = (values) =>
  Object.fromEntries(
    TABLES.map(({ id: tableId }) => [
      tableId,
      {
        disabled: TABLE_COLUMN_CATALOG[tableId]
          .map((column) => column.id)
          .filter((id) => values[tableId][id] === false)
      }
    ])
  )

/**
 * Partnerweite Grundlage für die Tabellenspalten auf /talents und /careers.
 * Deaktivierte Spalten sind für alle Nutzer ausgeblendet und lassen sich von
 * ihnen nicht einblenden. Sichtbarkeit und Reihenfolge der verfügbaren Spalten
 * stellt jeder Nutzer für sich in der Tabelle ein (useTableColumnSettings).
 * Ändern dürfen nur Admins, alle anderen sehen den Stand schreibgeschützt.
 */
const TableColumnDefaults = ({ className }) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const { partner } = usePartnerContext()
  const { activeSpace } = useUserContext()

  const hasAdminRole = activeSpace.authorization_role.id === ROLES.ADMIN.id

  const dashboardSettings = JSON.parse(
    partner.app_settings?.dashboard_settings ?? '{}'
  )
  const disabledByTable = readPartnerTableColumns(dashboardSettings)

  const { mutate } = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: toFormValues(disabledByTable),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true)
      trackEvent(trackEvents.UPDATE_DASHBOARD_TABLE_COLUMNS_SETTINGS)

      const app_settings = { ...(partner.app_settings || {}) }
      app_settings.dashboard_settings = JSON.stringify({
        ...dashboardSettings,
        [PARTNER_TABLE_COLUMNS_KEY]: toDisabledSettings(values)
      })

      mutate(
        { input: { id: partner.id, app_settings } },
        {
          onSuccess: () => {
            success(t(I18N_PREFIX + 'success'))
            setSubmitting(false)
          },
          onError: () => setSubmitting(false)
        }
      )
    }
  })

  return (
    <SettingsContainer
      className={className}
      title={t(I18N_PREFIX + 'title')}
      description={t(I18N_PREFIX + 'description')}
      onSubmit={hasAdminRole ? formik.handleSubmit : undefined}
      dirty={formik.dirty}
      isLoading={formik.isSubmitting}
    >
      {!hasAdminRole && (
        <p className='text-sm text-gray-500'>
          {t(I18N_PREFIX + 'admin_only_hint')}
        </p>
      )}
      <div
        className={
          hasAdminRole
            ? 'flex flex-col gap-y-10'
            : 'pointer-events-none flex flex-col gap-y-10 opacity-60'
        }
        aria-disabled={!hasAdminRole || undefined}
      >
        {TABLES.map(({ id: tableId, groupKey }) => (
          <div key={tableId}>
            <GroupLabel className='mb-4'>
              {t(I18N_PREFIX + groupKey)}
            </GroupLabel>
            <div className='flex flex-col gap-y-4'>
              {TABLE_COLUMN_CATALOG[tableId].map((column, index) => {
                const enabled = formik.values[tableId][column.id]

                return (
                  <Fragment key={column.id}>
                    <Toggle
                      appearance='with-left-label-and-description'
                      label={t(column.labelKey)}
                      description={t(
                        I18N_PREFIX + (enabled ? 'available' : 'disabled')
                      )}
                      enabled={enabled}
                      setEnabled={() =>
                        formik.setFieldValue(
                          `${tableId}.${column.id}`,
                          !enabled
                        )
                      }
                    />
                    {index < TABLE_COLUMN_CATALOG[tableId].length - 1 && (
                      <Divider marginY={0} />
                    )}
                  </Fragment>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </SettingsContainer>
  )
}

TableColumnDefaults.propTypes = {
  className: PropTypes.string
}

TableColumnDefaults.defaultProps = {
  className: undefined
}

export default TableColumnDefaults
